import React, { Suspense, useMemo, useState } from 'react';
import { Routes, Route, BrowserRouter, Navigate } from 'react-router-dom';

import { Global } from '@emotion/react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { useRecoilValue } from 'recoil';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';

import Login from '@pages/Login';
import MyPage from '@pages/MyPage';
import PasswordChange from '@pages/MyPage/pages/PasswordChange';
import SubmitLogin from '@pages/Submit';
import SubmitMain from '@pages/Submit/pages';

import Layout from '@layout/index';

import ContentsErrorBoundary from '@components/Common/Error/ContentsErrorBoundary';
import PageNotFound from '@components/Common/Error/PageNotFound';
import LoadingSpinner from '@components/Common/LoadingSpinner';
import SessionExpiredModal from '@components/Modals/SessionExpiredModal';

import reset from '@styles/reset';

import { auth } from '@constants/state';

import Auth from '@src/auth/Auth';

import SubmitAuth from './auth/SubmitAuth';
import CompanyRoute from './routes/CompanyRoute';
import MasterRoute from './routes/MasterRoute';
import PartnerRoute from './routes/PartnerRoute';

function App() {
  const { role } = useRecoilValue(auth);

  const [sessionExpiredModalOpen, setSessionExpiredModalOpen] =
    useState<boolean>(false);

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry: false,
        useErrorBoundary: true,
      },
    },
  });

  /** 계정별 home 주소 */
  const ACCOUNT_HOME_PATH = useMemo(() => {
    if (['ROLE_MARK', 'ROLE_GOWID'].includes(role || '')) {
      return '/client';
    } else if (role === 'ROLE_PARTNER') {
      return '/partner-group';
    } else {
      return '/home';
    }
  }, [role]);

  return (
    <>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <QueryParamProvider adapter={ReactRouter6Adapter}>
            <Global styles={reset} />
            <Routes>
              {/* SUBMIT ROUTE */}
              <Route path="submit" element={<SubmitLogin />} />
              <Route element={<SubmitAuth />}>
                <Route
                  path="submit"
                  element={<Navigate replace to="/submit/main" />}
                />
                <Route path="submit/main" element={<SubmitMain />} />
              </Route>
              {/* GMS ROUTE */}
              <Route path="login" element={<Login />} />
              <Route
                path="/"
                element={
                  <ContentsErrorBoundary>
                    <Suspense fallback={<LoadingSpinner />}>
                      <Layout />
                    </Suspense>
                  </ContentsErrorBoundary>
                }
              >
                <Route element={<Auth />}>
                  <Route
                    path="/"
                    element={<Navigate replace to={ACCOUNT_HOME_PATH} />}
                  />
                  <Route path="/mypage" element={<MyPage />}>
                    <Route
                      path=""
                      element={<Navigate replace to="password-change" />}
                    />
                    <Route
                      path="password-change"
                      element={<PasswordChange />}
                    />
                  </Route>
                  {/* 마스터 계정 Route */}
                  {['ROLE_MARK', 'ROLE_GOWID'].includes(role || '') &&
                    MasterRoute()}

                  {/* 관리자 계정 Route */}
                  {role === 'ROLE_COMPANY' && CompanyRoute()}

                  {/* 파트너 계정 Route */}
                  {role === 'ROLE_PARTNER' && PartnerRoute()}
                </Route>
              </Route>
              <Route path="*" element={<PageNotFound />} />
            </Routes>
            <SessionExpiredModal
              open={sessionExpiredModalOpen}
              handleGlobalState={setSessionExpiredModalOpen}
            />
          </QueryParamProvider>
        </BrowserRouter>
        <ReactQueryDevtools />
      </QueryClientProvider>
    </>
  );
}

export default App;
